import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button } from 'ui/button';
import { body2, Caption, colors, largeShadow, spacing, Subtitle1 } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { useDispatch } from 'react-redux';
import { useClickAway } from 'react-use';
import { addNegativeToast } from 'modules/toastContainer';
import { useGetAllUrlFilters } from '../hooks/useGetAllUrlFilters';
import { compileSharedUrl } from '../utils';

const Error = styled.div`
  margin-bottom: ${spacing.half};
`;

const Item = styled.li`
  display: list-item;
  list-style-type: disc;
`;

const PopUp = styled.div`
  background: ${colors.white};
  border-radius: ${spacing.half};
  margin-left: -${pxToRem(284)};
  margin-top: ${spacing.half};
  padding: ${spacing.plusHalf};
  position: absolute;
  width: ${pxToRem(380)};
  z-index: 10;
  ${largeShadow};
`;

const UrlWrapper = styled.div`
  align-items: center;
  border-radius: ${spacing.quarter};
  border: ${spacing.single} solid ${colors.grey15};
  display: flex;
  justify-content: space-between;
  margin-bottom: ${spacing.half};
  margin-top: ${spacing.base};
  padding: ${spacing.quarter} 0;
`;

const Url = styled.div`
  ${body2}
  margin-left: ${spacing.threeQuarter};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: ${pxToRem(244)};
`;

const StyledButton = styled(Button)`
  margin-right: ${spacing.eighth};
  width: ${pxToRem(68)};
`;

const StyledCaption = styled(Caption)`
  color: ${colors.textSecondary};
`;

type ShareUrlProps = {
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export const copy = {
  negativeMsg: 'Could not copy url to clipboard. Please try again.',
  headerText: 'Share your selected filters and date range',
  subtext: 'Only users in your Iterable project can view this link.',
  copied: 'Copied!',
  toCopy: 'Copy',
  urlTooLong:
    'Too many filters are currently selected. The following were excluded from the share URL:',
};

export const ShareUrlPopUp = ({ setIsOpen }: ShareUrlProps) => {
  const [copied, setCopied] = useState(false);
  const allFilters = useGetAllUrlFilters();
  const { completeUrl, error } = compileSharedUrl(allFilters);
  const dispatch = useDispatch();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(completeUrl);
      setCopied(true);
    } catch (err) {
      setCopied(false);
      dispatch(addNegativeToast(copy.negativeMsg));
    }
  };

  // close on click outside
  const wrapperRef = useRef(null);
  useClickAway(wrapperRef, () => {
    setIsOpen(false);
  });

  // url max length error
  useEffect(() => {
    if (error.length > 0) {
      setIsOpen(false);
      dispatch(
        addNegativeToast(
          <>
            <Error>{copy.urlTooLong}</Error>
            <ul>
              {error.map((e, i) => (
                <Item key={i}>{e}</Item>
              ))}
            </ul>
          </>,
        ),
      );
    }
  }, []);

  return (
    <PopUp ref={wrapperRef}>
      <Subtitle1>{copy.headerText}</Subtitle1>
      <UrlWrapper>
        <Url>{completeUrl}</Url>
        <StyledButton onClick={handleCopy}>
          <span>{copied ? copy.copied : copy.toCopy}</span>
        </StyledButton>
      </UrlWrapper>
      <StyledCaption>{copy.subtext}</StyledCaption>
    </PopUp>
  );
};
